export const BASE_URL = process.env.NEXT_PUBLIC_API_URL || "https://api.almasriamedwaste.com/api";

const FETCH_TIMEOUT_MS = 10000;

function fetchWithTimeout(url: string | URL | Request, init?: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  return fetch(url, { ...init, signal: controller.signal }).finally(() => clearTimeout(timeout));
}

// Types
export interface ServiceRequest {
  name: string;
  facility_type: string;
  email: string;
  phone: string;
  service_type: string;
  details?: string;
}

export interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt?: string;
  content?: string;
  published_at: string;
  author?: {
    name: string;
    avatar?: string;
    role?: string;
    bio?: string;
  };
  category?: string;
  cover_image: string;
}

export interface AppShowcase {
  screenshots: string[];
  download_counts: string | number;
  rating?: string | number;
  ios_url: string;
  android_url: string;
}

export interface Settings {
  phone: string | null;
  phones?: string | null;
  email: string | null;
  whatsapp?: string | null;
  whatsapp_api_key?: string | null;
  whatsapp_device_id?: string | null;
  tax?: string | null;
  tax_number?: string | null;
  ios_url?: string | null;
  android_url?: string | null;
  visit_fee?: number | null;
  waste_price_per_kg?: number | null;
  address:
    | { address: string | null; lat?: number | null; lng?: number | null }
    | string
    | null;
  logo?: string | null;
  social_links?:
    | {
        facebook?: string;
        twitter?: string;
        linkedin?: string;
        [key: string]: string | undefined;
      }
    | unknown[];
  seo?: {
    title: string | null;
    description: string | null;
    keywords?: string | null;
  };
}

export interface Stats {
  clients_count: number;
  waste_collected_kg: number;
  fleet_size: number;
  commitment_rate: number;
}

export interface Partner {
  name: string;
  logo: string;
}

export interface FAQ {
  title: string;
  content: string;
}

export interface GovHospital {
  id: number;
  name: string;
  type: "gov" | "uni" | "national";
  client_type?: "gov" | "private";
  logo?: string;
  specialization?: string;
  facility_name?: string;
}

const getHeaders = (locale: string = "ar") => ({
  Accept: "application/json",
  "Accept-Language": locale,
  "Content-Type": "application/json",
});

const STORAGE_BASE = "https://api.almasriamedwaste.com/storage";

export const ensureFullUrl = (path: string | null | undefined): string => {
  if (!path || typeof path !== "string") return "";
  let p = path.replace(/\\/g, "/");
  if (p.startsWith("http://api.almasriamedwaste.com")) {
    p = p.replace("http://", "https://");
  }
  if (p.startsWith("http")) return p;

  // Normalize slashes
  const normalizedPath = path.replace(/\\/g, "/");

  // If it starts with storage/, don't double it
  if (normalizedPath.startsWith("storage/")) {
    return `https://api.almasriamedwaste.com/${normalizedPath}`;
  }

  // Prepend storage base
  return `${STORAGE_BASE}/${normalizedPath.startsWith("/") ? normalizedPath.slice(1) : normalizedPath}`;
};

export const processHtmlImages = (html: string | null | undefined): string => {
  if (!html || typeof html !== "string") return "";

  // Fix cases where tags are missing opening '<' (seen in some raw outputs)
  let fixed = html.replace(
    /(^|[^<])(img|p|div|span|h[1-6]|ul|li|br|strong|em)([^a-z0-9])/gi,
    "$1<$2$3",
  );

  // Fix double opening brackets if they occur (e.g., <<p>)
  fixed = fixed.replace(/<+</g, "<");

  // Replace src="..." for paths starting with images/, general/, or storage/
  // Also handle backslashes in paths
  const imgRegex =
    /src\s*=\s*(["'])((?:images|general|storage)[/\\][^"']+)\1/gi;
  return fixed.replace(imgRegex, (match, quote, path) => {
    return `src=${quote}${ensureFullUrl(path)}${quote}`;
  });
};
const handleResponse = async (res: Response) => {
  const url = res.url;
  const contentType = res.headers.get("content-type");
  const isJson = contentType && contentType.includes("application/json");

  if (res.status === 404) {
    return null;
  }

  if (!res.ok) {
    if (isJson) {
        try {
            const errorJson = await res.json();
            const error = new Error(errorJson.message || `API_ERROR_${res.status}`);
            (error as any).status = errorJson.status;
            throw error;
        } catch (e) {
            if (e instanceof Error && e.message !== `API_ERROR_${res.status}` && !e.message.startsWith('API_ERROR')) {
                throw e;
            }
        }
    }
    const text = await res.text();
    throw new Error(`API_ERROR_${res.status}`);
  }

  if (!isJson) {
    return null;
  }

  return res.json();
};

// API Service
export const api = {
  // 1. Settings & SEO
  getSettings: async (locale?: string): Promise<Settings | null> => {
    try {
      const res = await fetchWithTimeout(`${BASE_URL}/settings`, {
        headers: getHeaders(locale),
        next: { revalidate: 3600 },
      });
      const json = await handleResponse(res);
      const data = json?.data || json;
      if (data && typeof data === "object") {
        return {
          ...data,
          logo: ensureFullUrl(data.logo),
        };
      }
      return data;
    } catch (error) {
      return null;
    }
  },

  // 2. Stats
  getStats: async (locale?: string): Promise<Stats | null> => {
    const res = await fetchWithTimeout(`${BASE_URL}/stats`, {
      headers: getHeaders(locale),
      next: { revalidate: 300 },
    });
    const json = await handleResponse(res);
    return json?.data || json;
  },

  // 3. Blog Posts
  getPosts: async (locale?: string, page = 1, limit = 9, category?: string) => {
    const categoryQuery = category ? `&category=${category}` : "";
    const res = await fetchWithTimeout(
      `${BASE_URL}/posts?page=${page}&limit=${limit}${categoryQuery}`,
      {
        headers: getHeaders(locale),
        next: { revalidate: 300 },
      },
    );
    const json = await handleResponse(res);
    if (!json) return { data: [], meta: {} };
    const rawData = json.data ? json.data : json;

    // Transform data
    const transformed = (Array.isArray(rawData) ? rawData : [rawData]).map(
      (post: BlogPost) => ({
        ...post,
        cover_image: ensureFullUrl(post.cover_image),
        excerpt: processHtmlImages(post.excerpt),
        content: processHtmlImages(post.content),
        author: post.author
          ? { ...post.author, avatar: ensureFullUrl(post.author.avatar) }
          : post.author,
      }),
    );

    return { data: transformed, meta: json.meta || {} };
  },

  getPost: async (
    id_or_slug: number | string,
    locale?: string,
  ): Promise<BlogPost | null> => {
    const res = await fetchWithTimeout(`${BASE_URL}/posts/${id_or_slug}`, {
      headers: getHeaders(locale),
      next: { revalidate: 300 },
    });
    const json = await handleResponse(res);
    const data = json?.data || json;
    if (data) {
      return {
        ...data,
        cover_image: ensureFullUrl(data.cover_image),
        excerpt: processHtmlImages(data.excerpt),
        content: processHtmlImages(data.content),
        author: data.author
          ? { ...data.author, avatar: ensureFullUrl(data.author.avatar) }
          : data.author,
      } as BlogPost;
    }
    return data;
  },

  // 4. Partners
  getPartners: async (locale?: string): Promise<Partner[]> => {
    const res = await fetchWithTimeout(`${BASE_URL}/partners`, {
      headers: getHeaders(locale),
      next: { revalidate: 3600 },
    });
    const json = await handleResponse(res);
    const rawData = json?.data || json;
    const data = Array.isArray(rawData) ? rawData : [];
    return data.map((p: Partner) => ({
      ...p,
      logo: ensureFullUrl(p.logo),
    }));
  },

  // 4b. Government & University Hospitals
  getGovHospitals: async (locale?: string): Promise<GovHospital[]> => {
    try {
      const res = await fetchWithTimeout(`${BASE_URL}/gov-hospitals`, {
        headers: getHeaders(locale),
        next: { revalidate: 3600 },
      });
      const json = await handleResponse(res);
      const rawData = json?.data || json;
      return Array.isArray(rawData) ? rawData : [];
    } catch (error) {
      return [];
    }
  },

  // 5. FAQs
  getFaqs: async (locale?: string): Promise<FAQ[]> => {
    const res = await fetchWithTimeout(`${BASE_URL}/faqs`, {
      headers: getHeaders(locale),
      next: { revalidate: 3600 },
    });
    const json = await handleResponse(res);
    const rawData = json?.data || json;
    const data = Array.isArray(rawData) ? rawData : [];
    return data.map((faq: FAQ) => ({
      ...faq,
      content: processHtmlImages(faq.content),
    }));
  },

  // 6. App Showcase
  getAppShowcase: async (locale?: string): Promise<AppShowcase | null> => {
    const res = await fetchWithTimeout(`${BASE_URL}/app-showcase`, {
      headers: getHeaders(locale),
      next: { revalidate: 3600 },
    });
    const json = await handleResponse(res);
    const data = json?.data || json;
    if (data) {
      return {
        ...data,
        screenshots: Array.isArray(data.screenshots)
          ? data.screenshots.map((s: string) => ensureFullUrl(s))
          : [],
      } as AppShowcase;
    }
    return data;
  },

  // 7. Service Request
  submitRequest: async (data: ServiceRequest, locale?: string) => {
    const res = await fetchWithTimeout(`${BASE_URL}/requests`, {
      method: "POST",
      headers: getHeaders(locale),
      body: JSON.stringify(data),
    });
    return handleResponse(res);
  },

  submitDeleteAccountRequest: async (
    data: { name: string; email: string; phone: string; reason: string },
    locale?: string
  ) => {
    const res = await fetchWithTimeout(`${BASE_URL}/requests`, {
      method: "POST",
      headers: getHeaders(locale),
      body: JSON.stringify({
        name: data.name,
        email: data.email,
        phone: data.phone,
        type: "complaint",
        subject: "Account Deletion Request - طلب حذف حساب",
        details: `Reason: ${data.reason || "Not specified"}\n\nUser confirmed that they read the warning and want to permanently delete their account and associated data.`,
      }),
    });
    return handleResponse(res);
  },

  // 8. Static Pages
  getPages: async (locale?: string): Promise<StaticPage[]> => {
    try {
      const res = await fetchWithTimeout(`${BASE_URL}/general/pages`, {
        headers: getHeaders(locale),
        next: { revalidate: 3600 },
      });
      const json = await handleResponse(res);
      const rawData = json?.data || json;
      const data = Array.isArray(rawData) ? rawData : [];
      return data.map((p: any) => ({
        ...p,
        slug: p.slug || p.key, // Map key to slug
        title: p.title || p.text, // Map text to title
        type: p.type || p.key,
      }));
    } catch (error) {
      return [];
    }
  },

  // 9. Static Page By Slug
  getPage: async (
    slug_or_type: string,
    locale?: string,
  ): Promise<StaticPage | null> => {
    if (!slug_or_type || slug_or_type === "undefined") return null;

    try {
      const res = await fetchWithTimeout(`${BASE_URL}/general/page/${slug_or_type}`, {
        headers: getHeaders(locale),
        next: { revalidate: 3600 },
      });
      const json = await handleResponse(res);
      const data = json?.data || json;
      if (data && typeof data === "object") {
        return {
          ...data,
          slug: data.slug || data.key,
          title: data.title || data.text,
          content: processHtmlImages(data.content),
          image: ensureFullUrl(data.image),
        } as StaticPage;
      }
      return null;
    } catch (error) {
      return null;
    }
  },
};

export interface StaticPage {
  title: string;
  slug: string;
  type: string;
  content?: string;
  image?: string;
}
